import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Percent,
  Edit2,
  Save,
  X,
  TrendingUp,
  IndianRupee,
  Info,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { pricingApi, type PricingRule } from '../api/pricing.api';
import toast from 'react-hot-toast';

const commissionSchema = z.object({
  commission_percent: z.coerce.number().min(0, 'Min 0%').max(100, 'Max 100%'),
  night_surcharge_percent: z.coerce.number().min(0).max(100),
});

type CommissionFormData = z.infer<typeof commissionSchema>;

const vehicleLabels: Record<string, string> = {
  bike: 'Bike',
  auto: 'Auto',
  mini_truck: 'Mini Truck',
  truck: 'Truck',
  tempo: 'Tempo',
};

function CommissionRow({ rule, onEdit }: { rule: PricingRule; onEdit: (rule: PricingRule) => void }) {
  const perKmRate = parseFloat(String(rule.per_km_rate));
  const commissionPct = parseFloat(String(rule.commission_percent));
  const nightSurchargePct = parseFloat(String(rule.night_surcharge_percent));
  const estimatedRevenue = (perKmRate * 10 * commissionPct) / 100;

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="font-medium">{vehicleLabels[rule.vehicle_type] || rule.vehicle_type}</span>
          <Badge variant={rule.is_active ? 'default' : 'secondary'} className="text-xs">
            {rule.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <span className="text-lg font-bold text-primary">{commissionPct}%</span>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm">{nightSurchargePct}%</span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground">
          ~{formatCurrency(estimatedRevenue)}/10km trip
        </span>
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onEdit(rule)}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function EditCommissionModal({
  rule,
  onClose,
}: {
  rule: PricingRule;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const { register, handleSubmit, formState: { errors } } = useForm<CommissionFormData>({
    resolver: zodResolver(commissionSchema),
    defaultValues: {
      commission_percent: parseFloat(String(rule.commission_percent)),
      night_surcharge_percent: parseFloat(String(rule.night_surcharge_percent)),
    },
  });

  const { mutate: update, isPending } = useMutation({
    mutationFn: (data: CommissionFormData) =>
      pricingApi.update(rule.id, { ...data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast.success('Commission updated successfully');
      onClose();
    },
    onError: () => toast.error('Failed to update commission'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">
            Edit Commission — {vehicleLabels[rule.vehicle_type] || rule.vehicle_type}
          </h3>
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <form onSubmit={handleSubmit((d) => update(d))} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="commission_percent" required>
              Platform Commission (%)
            </Label>
            <Input
              id="commission_percent"
              type="number"
              step="0.5"
              placeholder="e.g. 20"
              error={errors.commission_percent?.message}
              {...register('commission_percent')}
            />
            <p className="text-xs text-muted-foreground">
              Percentage of fare earned by the platform per trip.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="night_surcharge_percent">Night Surcharge (%)</Label>
            <Input
              id="night_surcharge_percent"
              type="number"
              step="0.5"
              placeholder="e.g. 15"
              error={errors.night_surcharge_percent?.message}
              {...register('night_surcharge_percent')}
            />
            <p className="text-xs text-muted-foreground">
              Extra % added to fare for trips between 10 PM – 6 AM.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isPending}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function CommissionPage() {
  const [editingRule, setEditingRule] = useState<PricingRule | null>(null);

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['pricing-rules'],
    queryFn: pricingApi.getAll,
  });

  const activeRules = rules.filter((r) => r.is_active);
  const avgCommission =
    activeRules.length > 0
      ? (activeRules.reduce((sum, r) => sum + parseFloat(String(r.commission_percent)), 0) / activeRules.length).toFixed(1)
      : '0';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Commission Management"
        description="Configure platform commission rates per vehicle type"
      />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-full bg-primary/10 p-3">
              <Percent className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Avg. Commission</p>
              <p className="text-2xl font-bold">{avgCommission}%</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-full bg-green-100 p-3">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active Vehicle Types</p>
              <p className="text-2xl font-bold">{activeRules.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-full bg-amber-100 p-3">
              <IndianRupee className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Max Commission</p>
              <p className="text-2xl font-bold">
                {Math.max(...rules.map((r) => parseFloat(String(r.commission_percent))), 0)}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5" />
            Commission by Vehicle Type
            <Tooltip content="Commission % is deducted from the driver's earnings per trip. Night surcharge applies between 10 PM and 6 AM.">
              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
            </Tooltip>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Percent className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No pricing rules found. Go to Fare Rules to create them first.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle Type</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Night Surcharge</TableHead>
                  <TableHead>Est. Platform Revenue</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <CommissionRow
                    key={rule.id}
                    rule={rule}
                    onEdit={setEditingRule}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {editingRule && (
        <EditCommissionModal
          rule={editingRule}
          onClose={() => setEditingRule(null)}
        />
      )}
    </div>
  );
}
